import React, { useMemo, useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Box,
  FormControlLabel,
  Alert,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
} from '@mui/material';
import Toggle from '../../../../../common/components/Toggle/Toggle';
import RenderSelect from '../../../../../common/components/Form/RenderSelect';

import { DEFAULT_COMPONENTS, DEFAULT_EVENT_TYPES, DEFAULT_METRICS, LOG_LEVEL_OPTIONS } from '../../../shared/constants';
import { UI_TEXT } from '../../../mocks/mockConfigurationTab';
import StatusBadge from '../../../shared/components/StatusBadge/StatusBadge';
import { createListCheckboxHandler, createNumericFieldHandler, createToggleSectionHandler, disabledCardSx } from '../lib/edgeControllerLib';

const INITIAL_CONFIG = {
  telemetry: {
    enabled: true,
    aggregationIntervalSeconds: 60,
    metricMode: 'all',
    selectedMetrics: ['POWER_W', 'VOLTAGE_V'],
  },
  logging: {
    replicationEnabled: true,
    globalLogLevel: 'INFO',
    components: DEFAULT_COMPONENTS.reduce(
      (acc, name) => ({
        ...acc,
        [name]: name === 'com.iotecha.greengrass.em.EventAdapter' ? 'DEBUG' : 'INFO',
      }),
      {},
    ),
  },
  events: {
    enabled: true,
    uploadIntervalSeconds: 30,
    selectedEventTypes: ['CAR_PLUG_IN', 'OVERLOAD_PROTECTION'],
  },
};

function ConfigurationTab() {
  const [config, setConfig] = useState(INITIAL_CONFIG);
  const [activeSection, setActiveSection] = useState('telemetry');

  const handleSectionToggle = useMemo(() => createToggleSectionHandler(setConfig), []);

  const metrics = useMemo(() => DEFAULT_METRICS, []);
  const eventTypes = useMemo(() => DEFAULT_EVENT_TYPES, []);
  const componentNames = useMemo(() => Object.keys(config.logging.components), [config.logging.components]);
  const metricOptions = useMemo(() => metrics.map((name) => ({ label: name, value: name })), [metrics]);

  const metricSelectValue = useMemo(() => {
    const { metricMode, selectedMetrics } = config.telemetry;
    return metricMode === 'all' ? metricOptions.map((opt) => opt.value) : selectedMetrics || [];
  }, [config.telemetry.metricMode, config.telemetry.selectedMetrics, metricOptions]);

  const handleTelemetryMetricsChange = useCallback(
    (value) => {
      const nextMetrics = Array.isArray(value) ? value : [];
      setConfig((prev) => ({
        ...prev,
        telemetry: {
          ...prev.telemetry,
          metricMode: nextMetrics.length === metricOptions.length ? 'all' : 'list',
          selectedMetrics: nextMetrics,
        },
      }));
    },
    [metricOptions.length],
  );

  const handleGlobalLogLevelChange = useCallback((value) => {
    setConfig((prev) => ({ ...prev, logging: { ...prev.logging, globalLogLevel: value } }));
  }, []);

  const handleComponentLogLevelChange = useCallback(
    (componentName) => (value) => {
      setConfig((prev) => ({
        ...prev,
        logging: {
          ...prev.logging,
          components: { ...prev.logging.components, [componentName]: value },
        },
      }));
    },
    [],
  );

  const handleTelemetryAggregationIntervalChange = useMemo(
    () =>
      createNumericFieldHandler(setConfig, { min: 5, max: 3600, fallback: 60 }, (prev, next) => ({
        ...prev,
        telemetry: { ...prev.telemetry, aggregationIntervalSeconds: next },
      })),
    [],
  );

  const handleEventsUploadIntervalChange = useMemo(
    () =>
      createNumericFieldHandler(setConfig, { min: 5, max: 3600, fallback: 30 }, (prev, next) => ({
        ...prev,
        events: { ...prev.events, uploadIntervalSeconds: next },
      })),
    [],
  );

  const handleEventTypeCheckboxChange = useMemo(
    () =>
      createListCheckboxHandler(
        setConfig,
        (prev) => prev.events.selectedEventTypes,
        (prev, next) => ({ ...prev, events: { ...prev.events, selectedEventTypes: next } }),
      ),
    [],
  );

  const impactEstimations = useMemo(() => {
    const { telemetry: telemetryConfig, logging: loggingConfig, events: eventsConfig } = config;

    return {
      telemetry: telemetryConfig.enabled
        ? (() => {
            const interval = telemetryConfig.aggregationIntervalSeconds || 60;
            const metricCount = telemetryConfig.metricMode === 'all' ? metrics.length : telemetryConfig.selectedMetrics.length;
            const kbPerHour = Math.round(((300 + metricCount * 150) * Math.floor(3600 / Math.max(interval, 1))) / 1024);
            return {
              label: `Estimated usage: ~${kbPerHour} KB/hour`,
              detail: `${metricCount} metric(s) every ${interval}s (demo estimate).`,
            };
          })()
        : { label: UI_TEXT.telemetry.telemetryDisabledUsageLabel, detail: UI_TEXT.telemetry.telemetryDisabledUsageDetail },

      logging: loggingConfig.replicationEnabled
        ? loggingConfig.globalLogLevel === 'DEBUG' || Object.values(loggingConfig.components).some((logLevel) => logLevel === 'DEBUG')
          ? { label: UI_TEXT.logging.debugUsageLabel, detail: UI_TEXT.logging.debugUsageDetail }
          : { label: UI_TEXT.logging.moderateUsageLabel, detail: UI_TEXT.logging.moderateUsageDetail }
        : { label: UI_TEXT.logging.replicationDisabledUsageLabel, detail: UI_TEXT.logging.replicationDisabledUsageDetail },

      events: eventsConfig.enabled
        ? {
            label: `Uploads every ${eventsConfig.uploadIntervalSeconds || 30}s`,
            detail: `${eventsConfig.selectedEventTypes.length} event type(s) enabled (demo).`,
          }
        : { label: UI_TEXT.events.reportingDisabledUsageLabel, detail: UI_TEXT.events.reportingDisabledUsageDetail },
    };
  }, [
    config.telemetry.enabled,
    config.telemetry.aggregationIntervalSeconds,
    config.telemetry.metricMode,
    config.telemetry.selectedMetrics.length,
    config.logging.replicationEnabled,
    config.logging.globalLogLevel,
    config.logging.components,
    config.events.enabled,
    config.events.uploadIntervalSeconds,
    config.events.selectedEventTypes.length,
    metrics.length,
  ]);

  const sections = useMemo(
    () => [
      { id: 'telemetry', title: UI_TEXT.telemetry.title, enabled: config.telemetry.enabled },
      { id: 'logging', title: UI_TEXT.logging.title, enabled: config.logging.replicationEnabled },
      { id: 'events', title: UI_TEXT.events.title, enabled: config.events.enabled },
    ],
    [config.telemetry.enabled, config.logging.replicationEnabled, config.events.enabled],
  );

  const SectionCard = useCallback(
    ({ title, subtitle, isEnabled, togglePath, children }) => (
      <Card className="configuration-section" sx={!isEnabled && disabledCardSx}>
        <CardContent>
          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h6">{title}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {subtitle}
                </Typography>
              </Box>
              <Toggle toggled={isEnabled} onToggle={handleSectionToggle(...togglePath)} />
            </Stack>
            <Stack spacing={2}>{children}</Stack>
          </Stack>
        </CardContent>
      </Card>
    ),
    [handleSectionToggle],
  );

  const ImpactAlert = useCallback(
    ({ estimation }) => (
      <Alert severity="info">
        <Typography variant="body2" fontWeight={600}>
          {estimation.label}
        </Typography>
        <Typography variant="caption" display="block">
          {estimation.detail}
        </Typography>
      </Alert>
    ),
    [],
  );

  const renderTelemetrySection = useCallback(
    () => (
      <SectionCard
        title={UI_TEXT.telemetry.title}
        subtitle={UI_TEXT.telemetry.subtitle}
        isEnabled={config.telemetry.enabled}
        togglePath={['telemetry', 'enabled']}
      >
        <TextField
          label={UI_TEXT.telemetry.aggregationIntervalLabel}
          helperText={UI_TEXT.telemetry.aggregationIntervalHelper}
          type="number"
          size="small"
          disabled={!config.telemetry.enabled}
          value={config.telemetry.aggregationIntervalSeconds}
          onChange={handleTelemetryAggregationIntervalChange}
          inputProps={{ min: 5, max: 3600 }}
          fullWidth
        />
        <ImpactAlert estimation={impactEstimations.telemetry} />
        <Box>
          <RenderSelect
            input={{
              name: 'telemetryMetrics',
              value: metricSelectValue,
              onChange: handleTelemetryMetricsChange,
            }}
            meta={{ touched: false, error: null }}
            label={UI_TEXT.telemetry.metricsLabel}
            options={metricOptions}
            fullWidth
            multiple
            showCheckboxes
            withSelectAll
            disabled={!config.telemetry.enabled}
          />
        </Box>
        {config.telemetry.metricMode === 'list' && !config.telemetry.selectedMetrics?.length && (
          <Typography variant="caption" color="error">
            {UI_TEXT.telemetry.metricsEmptyError}
          </Typography>
        )}
      </SectionCard>
    ),
    [
      config.telemetry,
      handleTelemetryAggregationIntervalChange,
      metricSelectValue,
      handleTelemetryMetricsChange,
      metricOptions,
      impactEstimations.telemetry,
      SectionCard,
      ImpactAlert,
    ],
  );

  const renderLoggingSection = useCallback(
    () => (
      <SectionCard
        title={UI_TEXT.logging.title}
        subtitle={UI_TEXT.logging.subtitle}
        isEnabled={config.logging.replicationEnabled}
        togglePath={['logging', 'replicationEnabled']}
      >
        <RenderSelect
          input={{
            name: 'globalLogLevel',
            value: config.logging.globalLogLevel,
            onChange: handleGlobalLogLevelChange,
          }}
          meta={{ touched: false, error: null }}
          label={UI_TEXT.logging.globalLogLevelLabel}
          options={LOG_LEVEL_OPTIONS}
          fullWidth
          disabled={!config.logging.replicationEnabled}
        />
        <ImpactAlert estimation={impactEstimations.logging} />
        <Box>
          <Typography variant="subtitle2">{UI_TEXT.logging.perComponentTitle}</Typography>
          <Typography variant="caption" color="textSecondary">
            Override log levels for individual services.
          </Typography>
        </Box>
        {componentNames.map((name) => (
          <Stack spacing={1.5} key={name}>
            <RenderSelect
              input={{
                name: `${name}-log-level`,
                value: config.logging.components[name],
                onChange: handleComponentLogLevelChange(name),
              }}
              meta={{ touched: false, error: null }}
              label={name}
              options={LOG_LEVEL_OPTIONS}
              fullWidth
              disabled={!config.logging.replicationEnabled}
            />
          </Stack>
        ))}
      </SectionCard>
    ),
    [
      config.logging,
      handleGlobalLogLevelChange,
      componentNames,
      handleComponentLogLevelChange,
      impactEstimations.logging,
      SectionCard,
      ImpactAlert,
    ],
  );

  const renderEventsSection = useCallback(
    () => (
      <SectionCard
        title={UI_TEXT.events.title}
        subtitle={UI_TEXT.events.subtitle}
        isEnabled={config.events.enabled}
        togglePath={['events', 'enabled']}
      >
        <Stack spacing={1}>
          <Box>
            <Typography variant="subtitle2">{UI_TEXT.events.eventTypesTitle}</Typography>
            <Typography variant="caption" color="textSecondary">
              Choose which events are uploaded.
            </Typography>
          </Box>
          <Box
            sx={{
              borderRadius: 1,
              py: 1,
              px: 2,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              backgroundColor: 'rgba(39, 94, 99, 0.06)',
            }}
          >
            {eventTypes.map((eventType) => (
              <FormControlLabel
                key={eventType}
                control={
                  <Checkbox
                    disabled={!config.events.enabled}
                    checked={config.events.selectedEventTypes.includes(eventType)}
                    onChange={handleEventTypeCheckboxChange(eventType)}
                  />
                }
                label={eventType}
              />
            ))}
          </Box>
          {!config.events.selectedEventTypes.length && (
            <Typography variant="caption" color="error">
              {UI_TEXT.events.emptyError}
            </Typography>
          )}
        </Stack>
        <TextField
          label={UI_TEXT.events.uploadIntervalLabel}
          helperText={UI_TEXT.events.uploadIntervalHelper}
          type="number"
          size="small"
          disabled={!config.events.enabled}
          value={config.events.uploadIntervalSeconds}
          onChange={handleEventsUploadIntervalChange}
          inputProps={{ min: 5, max: 3600 }}
          fullWidth
        />
        <ImpactAlert estimation={impactEstimations.events} />
      </SectionCard>
    ),
    [
      config.events,
      handleEventsUploadIntervalChange,
      eventTypes,
      handleEventTypeCheckboxChange,
      impactEstimations.events,
      SectionCard,
      ImpactAlert,
    ],
  );

  const sectionRenderers = useMemo(
    () => ({
      telemetry: renderTelemetrySection,
      logging: renderLoggingSection,
      events: renderEventsSection,
    }),
    [renderTelemetrySection, renderLoggingSection, renderEventsSection],
  );

  return (
    <div className="configuration-tab charger-configuration" style={{ height: '100%' }}>
      <div className="charger-configuration-pane" style={{ width: '20%', flexShrink: 0 }}>
        <List component="nav">
          {sections.map((section) => (
            <ListItem key={section.id} disablePadding>
              <ListItemButton selected={activeSection === section.id} onClick={() => setActiveSection(section.id)}>
                <ListItemText
                  primary={section.title}
                  secondary={
                    <StatusBadge
                      status={section.enabled ? 'Enabled' : 'Disabled'}
                      statusClassName={section.enabled ? 'status-enabled' : 'status-disabled'}
                    />
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </div>

      <div className="charger-configuration-pane charger-configuration-field-pane dark-background" style={{ flexGrow: 1 }}>
        <Container maxWidth="lg" style={{ maxWidth: 'unset', paddingLeft: 5, paddingRight: 5 }}>
          {sectionRenderers[activeSection]?.() || sectionRenderers.telemetry()}
        </Container>
      </div>
    </div>
  );
}

export default ConfigurationTab;
